import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FiCalendar,
  FiClock,
  FiTrash2,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import "./RemoveSchedule.css";

const RemoveSchedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paperTitles, setPaperTitles] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  // Fetch paper title by ID
  const fetchPaperTitle = useCallback(async (paperId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/get-paper/${paperId}`
      );
      return response.data.data.title;
    } catch (error) {
      console.error(`Error fetching title for paper ${paperId}:`, error);
      return `Paper ${paperId}`;
    }
  }, []);

  // Load schedules and paper titles
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      // Fetch schedules
      const schedulesResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/schedules`
      );

      // Create a map for paper titles
      const titlesMap = {};
      const uniquePaperIds = [
        ...new Set(schedulesResponse.data.flatMap((s) => s.papers)),
      ];

      // Fetch all paper titles in parallel
      const titlePromises = uniquePaperIds.map(async (paperId) => {
        titlesMap[paperId] = await fetchPaperTitle(paperId);
      });

      await Promise.all(titlePromises);
      setPaperTitles(titlesMap);

      // Transform schedules data with titles
      const transformedSchedules = schedulesResponse.data.map((schedule) => {
        const dateStr = new Date(schedule.date).toISOString().split("T")[0];
        const papersWithTitles = schedule.papers.map((paperId) => ({
          paperId,
          title: titlesMap[paperId] || `Paper ${paperId}`,
        }));

        return {
          ...schedule,
          date: dateStr,
          papers: papersWithTitles,
        };
      });

      setSchedules(transformedSchedules);
    } catch (error) {
      console.error("Error fetching schedules:", error);
      setError("Failed to load schedules. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [fetchPaperTitle]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);  const handleRemove = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(
            `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/schedules/${id}`
          );

          setSchedules(schedules.filter((schedule) => schedule._id !== id));

          Swal.fire({
            icon: "success",
            title: "Schedule Removed!",
            text: "The session schedule has been removed successfully.",
          });
        } catch (error) {
          console.error("Error removing schedule:", error);
          Swal.fire({
            icon: "error",
            title: "Failed!",
            text: error.response?.data?.message || "Failed to remove schedule.",
          });
        }
      }
    });
  };

  const formatTime = (time) => {
    if (!time) return "N/A";

    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const suffix = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;

    return `${displayHour}:${minutes} ${suffix}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Sort schedules chronologically
  const sortedSchedules = [...schedules].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  if (loading) {
    return (
      <div className="paper-list-wrapper">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading schedules...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="paper-list-wrapper">
        <div className="error-container">
          <div className="error-icon">!</div>
          <p className="error-message">{error}</p>
          <button className="retry-button" onClick={fetchData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="paper-list-wrapper">
      <div className="venue-list-container">
        <div className="header-section">
          <h1 className="paper-list-title">Schedule Management</h1>
          <p className="paper-list-subtitle">
            View and remove presentation schedules from the system
          </p>
        </div>

      {sortedSchedules.length > 0 ? (
        <div className="remove-schedule-table-container">
          <table className="remove-schedule-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Session</th>
                <th>Type</th>
                <th>Time</th>
                <th>Venue</th>
                <th>Room</th>
                <th>Papers</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedSchedules.map((schedule) => {
                const isExpanded = expandedRows[schedule._id];
                const sessionDate = new Date(schedule.date);
                const today = new Date();
                const isToday =
                  sessionDate.toDateString() === today.toDateString();

                return (
                  <React.Fragment key={schedule._id}>
                    <tr className={`schedule-row ${isToday ? "today" : ""}`}>
                      <td className="remove-schedule-date-cell">
                        <div className="remove-schedule-date-wrapper">
                          <FiCalendar className="remove-schedule-date-icon" />
                          <span>{formatDate(schedule.date)}</span>
                          {isToday && (
                            <span className="remove-schedule-today-badge">Today</span>
                          )}
                        </div>
                      </td>
                      <td className="remove-schedule-session-cell">
                        <div className="remove-schedule-session-name">{schedule.session}</div>
                      </td>
                      <td>
                        <span
                          className={`type-tag ${schedule.presentationType.toLowerCase()}`}
                        >
                          {schedule.presentationType}
                        </span>
                      </td>
                      <td className="remove-schedule-time-cell">
                        <div className="remove-schedule-time-range">
                          <FiClock className="remove-schedule-time-icon" />
                          <span>
                            {formatTime(schedule.timeFrom)} -{" "}
                            {formatTime(schedule.timeTo)}
                          </span>
                        </div>
                      </td>
                      <td className="remove-schedule-venue">{schedule.venue}</td>
                      <td className="remove-schedule-room">{schedule.room}</td>
                      <td className="remove-schedule-papers-count">
                        {schedule.papers.length} paper
                        {schedule.papers.length !== 1 ? "s" : ""}
                      </td>
                      <td className="remove-schedule-actions-cell">
                        <button
                          className="remove-schedule-remove-btn"
                          onClick={() => handleRemove(schedule._id)}
                        >
                          <FiTrash2 />
                        </button>
                        <button
                          className="remove-schedule-expand-btn"
                          onClick={() => toggleRow(schedule._id)}
                        >
                          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                        </button>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="remove-schedule-details-row">
                        <td colSpan="8">
                          <div className="remove-schedule-paper-details">
                            <h4>Papers in this Session</h4>
                            <div className="remove-schedule-papers-table-container">
                              <table className="remove-schedule-papers-table">
                                <thead>
                                  <tr>
                                    <th className="remove-schedule-paper-id-header">
                                      Paper ID
                                    </th>
                                    <th className="remove-schedule-paper-title-header">
                                      Paper Title
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {schedule.papers.map((paper) => (
                                    <tr key={paper.paperId}>
                                      <td className="remove-schedule-paper-id">
                                        {paper.paperId}
                                      </td>
                                      <td className="remove-schedule-paper-title">
                                        {paper.title}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-venues-box">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" viewBox="0 0 16 16">
            <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1z"/>
            <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
          </svg>
          <h3>No Schedules Found</h3>
          <p>There are currently no scheduled presentations added to the system.</p>
        </div>
      )}
      </div>
    </div>
  );
};

export default RemoveSchedule;
